import { useQuery } from '@tanstack/react-query';
import type { PurchasingTaxProfile } from '@optiflow/types';
import { api } from '@/services/apiClient';

export const purchasingTaxProfileKeys = {
  all: ['purchasing', 'tax-profiles'] as const,
};

/**
 * The tax profiles a purchase order line may be bought under.
 *
 * Purchasing's own route, so a buyer never needs a sales permission to raise a correct order. The
 * rows change about as often as the tax law does, so they are cached for the session rather than
 * refetched every time a line is added.
 */
export function usePurchasingTaxProfilesQuery({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: purchasingTaxProfileKeys.all,
    queryFn: () => api.get<PurchasingTaxProfile[]>('/purchasing/tax-profiles'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/** How a profile reads in a picker: its name, and what it actually costs. */
export function taxProfileLabel(
  profile: PurchasingTaxProfile,
  locale: string,
  treatmentLabel: (treatment: string) => string,
): string {
  const name = locale.startsWith('ar') ? (profile.nameAr ?? profile.nameEn) : profile.nameEn;
  if (profile.rateBasisPoints === 0) return `${name} — ${treatmentLabel(profile.treatment)}`;

  const percent = profile.rateBasisPoints / 100;
  return `${name} — ${percent}%`;
}
